import type { AxiosInstance, AxiosResponse, AxiosStatic, Canceler } from 'axios';
import { SearchParamNames } from '../models/enums';
import { LinkModel, SearchProductTileModel } from '../models/globals';

let axiosInstance: AxiosStatic | undefined;
let client: AxiosInstance | undefined;

const getAxios = (): AxiosStatic => {
    if (!axiosInstance) {
        // Keep axios out of SSR bundle initialization. Its browser build touches window/self at import time.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const axiosModule = require('axios') as AxiosStatic & { default?: AxiosStatic };
        axiosInstance = axiosModule.default || axiosModule;
    }

    return axiosInstance;
};

const getClient = (): AxiosInstance => {
    const axios = getAxios();

    if (!client) {
        client = axios.create();
        client.defaults.timeout = 30 * 1000;

        client.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error?.toJSON) {
                    console.error(error.toJSON());
                }
                throw error;
            },
        );
    }

    return client;
};

interface CancelableRequest<T> {
    cancel: Canceler;
    isCancel: AxiosStatic['isCancel'];
    promise: Promise<AxiosResponse<T>>;
}

const get = <T>(url: string): CancelableRequest<T> => {
    const axios = getAxios();
    const client = getClient();
    const { cancel, token: cancelToken } = axios.CancelToken.source();

    return {
        cancel,
        isCancel: axios.isCancel,
        promise: client.get<T>(url, { cancelToken }),
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const post = <T>(url: string, data?: any): CancelableRequest<T> => {
    const axios = getAxios();
    const client = getClient();
    const { cancel, token: cancelToken } = axios.CancelToken.source();

    return {
        cancel,
        isCancel: axios.isCancel,
        promise: client.post<T>(url, data, { cancelToken }),
    };
};

interface AutocompleteSearchResult {
    productLinks: LinkModel[];
    terms: string;
}

interface FindASchoolAutocompleteResult {
    type: 'PartnerOIC' | 'PartnerCTP' | 'Location';
    name: string;
    url: string;
}

interface FindASchoolStateLink {
    name: string;
    slug: string;
    abbreviation: string;
    url: string;
}

interface FindASchoolLocationLink {
    name: string;
    url: string;
}

const getAutocompleteSearchResult = (
    autocompleteApiPath: string,
    terms: string,
): CancelableRequest<AutocompleteSearchResult> => {
    const { promise, ...request } = get<AutocompleteSearchResult>(
        `${autocompleteApiPath}?${new URLSearchParams({ [SearchParamNames.Query]: terms })}`,
    );

    return {
        ...request,
        promise: promise.then((response) => {
            if (!response.data.productLinks) {
                const error = new Error('Unexpected response');

                console.error(error, response);

                throw error;
            }

            return response;
        }),
    };
};

const validateArrayResponse = <T>(response: AxiosResponse<T[]>): AxiosResponse<T[]> => {
    if (!Array.isArray(response.data)) {
        const error = new Error('Unexpected response');

        console.error(error, response);

        throw error;
    }

    return response;
};

const getFindASchoolAutocompleteResult = (
    autocompleteApiPath: string,
    terms: string,
): CancelableRequest<FindASchoolAutocompleteResult[]> => {
    const { promise, ...request } = get<FindASchoolAutocompleteResult[]>(
        `${autocompleteApiPath}?${new URLSearchParams({ name: terms })}`,
    );

    return {
        ...request,
        promise: promise.then(validateArrayResponse),
    };
};

const getFindASchoolCategoryStateLinks = (
    categoryStatesApiPath: string,
    categoryId: number,
): CancelableRequest<FindASchoolStateLink[]> => {
    const { promise, ...request } = get<FindASchoolStateLink[]>(
        `${categoryStatesApiPath}?${new URLSearchParams({ category: `${categoryId}` })}`,
    );

    return {
        ...request,
        promise: promise.then(validateArrayResponse),
    };
};

const getFindASchoolCategoryCityLinks = (
    categoryCitiesApiPath: string,
    categoryId: number,
    stateAbbreviation: string,
): CancelableRequest<FindASchoolLocationLink[]> => {
    const { promise, ...request } = get<FindASchoolLocationLink[]>(
        `${categoryCitiesApiPath}?${new URLSearchParams({ category: `${categoryId}`, state: stateAbbreviation })}`,
    );

    return {
        ...request,
        promise: promise.then(validateArrayResponse),
    };
};

interface ProductSearchResult {
    productTiles: SearchProductTileModel[];
    terms: string;
}

const getProductSearchResult = (
    resultsApiPath: string,
    terms: string,
    courseTypes: Set<string>,
    subTopics: Set<string>,
    ext: string,
): CancelableRequest<ProductSearchResult> => {
    const searchParams = new URLSearchParams({ [SearchParamNames.Query]: terms });
    courseTypes.forEach((type) => searchParams.append(SearchParamNames.CourseTypes, type));
    subTopics.forEach((code) => searchParams.append(SearchParamNames.SubTopics, code));
    searchParams.append(SearchParamNames.Ext, ext);
    const { promise, ...request } = get<ProductSearchResult>(`${resultsApiPath}?${searchParams}`);

    return {
        ...request,
        promise: promise.then((response) => {
            if (!response.data.productTiles) {
                const error = new Error('Unexpected response');

                console.error(error, response);

                throw error;
            }

            return response;
        }),
    };
};

type VimeoUrlResponse = unknown;

const checkIfVimeoVideoExists = (vimeoUrl: string): CancelableRequest<VimeoUrlResponse> => {
    return get<VimeoUrlResponse>(`https://vimeo.com/api/oembed.json?url=${vimeoUrl}`);
};

interface LeadFormResponse {
    analyticsData: {
        client_id: string; // Hashed email from form input
    };
}

const postLeadForm = (leadCreationApiPath: string, data: string): CancelableRequest<LeadFormResponse> => {
    return post<LeadFormResponse>(leadCreationApiPath, data);
};

const xhrClient = {
    getAutocompleteSearchResult,
    getFindASchoolAutocompleteResult,
    getFindASchoolCategoryStateLinks,
    getFindASchoolCategoryCityLinks,
    getProductSearchResult,
    checkIfVimeoVideoExists,
    postLeadForm,
};

export {
    xhrClient,
    CancelableRequest,
    AutocompleteSearchResult,
    FindASchoolAutocompleteResult,
    FindASchoolStateLink,
    FindASchoolLocationLink,
    ProductSearchResult,
    VimeoUrlResponse,
    LeadFormResponse,
};
